import { RangePickerProps } from 'antd/es/date-picker';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { ArgsType, ID } from '@/root/global';
import { FormInstance } from 'antd';

export function assertUnreachable(x: never): never {
  throw new Error("Didn't expect to get here");
}

export const COMPANY_NAME = '成都市悦肤源美容服务有限公司';
export function copyToClipboard(text: string) {
  let dummy = document.createElement('textarea');
  // to avoid breaking orgain page when copying more words
  // cant copy when adding below this code
  // dummy.style.display = 'none'
  document.body.appendChild(dummy);
  //Be careful if you use texarea. setAttribute('value', value), which works with "input" does not work with "textarea". – Eduard
  dummy.value = text;
  dummy.select();
  document.execCommand('copy');
  document.body.removeChild(dummy);
}

export function convertToHttps(url: string) {
  if (!url) return ``;

  return url.replace(/^http:\/\//, 'https://');
}

export const isNotEmptyArray = <T>(obj: T | T[]): obj is T[] => {
  return Array.isArray(obj) && obj.length > 0;
};
export const isEmptyObj = <T extends object>(obj: T) => {
  return obj && Object.keys(obj).length < 1;
};

/**
 * Regex of exact mobile.
 * <p>china mobile: 134(0-8), 135, 136, 137, 138, 139, 147, 150, 151, 152, 157, 158, 159, 178, 182, 183, 184,
 * 187, 188, 198</p>
 * <p>china unicom: 130, 131, 132, 145, 155, 156, 166, 171, 175, 176, 185, 186</p>
 * <p>china telecom: 133, 153, 173, 177, 180, 181, 189, 199</p>
 * <p>global star: 1349</p>
 * <p>virtual operator: 170</p>
 */
export const REGEX_MOBILE_EXACT =
  /^((13[0-9])|(14[5,7])|(15[0-3,5-9])|(16[6])|(17[0,1,3,5-8])|(18[0-9])|(19[8,9]))\d{8}$/;
export const REGEX_MOBILE = /1\d{10}$/;
export const SENSITIVE_FORM_PROPS = {
  autoComplete: 'new-password',
};

export const DATE_PICK_RANGES: RangePickerProps['ranges'] = {
  昨日: [dayjs().subtract(1, 'day'), dayjs().subtract(1, 'day')],
  上周: [dayjs().subtract(1, 'week').startOf('week'), dayjs().subtract(1, 'week').endOf('week')],
  本周: [dayjs().startOf('week'), dayjs().subtract(1, 'day')],
  上月: [
    dayjs().subtract(1, 'month').startOf('month'),
    dayjs().subtract(1, 'month').endOf('month'),
  ],
  本月: [dayjs().startOf('month'), dayjs().subtract(1, 'day')],
};

export const countCharLength = (str: string) => {
  if (!str) return 0;
  return str.replace(/[^\x00-\xff]/g, '01').length;
};

export const sleep = async (ms: number) =>
  new Promise(resolve => {
    setTimeout(resolve, ms);
  });

export const throttle = (fn: (...args) => unknown, delay: number) => {
  let t1 = 0;

  return (...args: ArgsType<typeof fn>) => {
    let t2 = new Date().getTime();
    if (t2 - t1 < delay) {
      return;
    } else {
      t1 = t2;
      return fn(...args);
    }
  };
};

export const debounce = (fn: Function, delay: number) => {
  let timer: number;
  delay = isNaN(delay) ? 0 : delay;
  return (...args) => {
    if (timer) {
      clearTimeout(timer);
    }
    timer = setTimeout(fn, delay, ...args);
  };
};

export const getDefaultIfUndefined = (value: unknown, defaultValue: unknown) => {
  if (value === undefined) {
    return defaultValue;
  }
  return value;
};

export const isValidNum = (v: string | number) => {
  return !isNaN(v as number) && v !== null && v !== '';
};

export const isNumber = (value: unknown): value is number => {
  return !isNaN(parseFloat(`${value}`)) && !isNaN(Number(value));
};

export type TimeRangeType = {
  startTime: string | dayjs.Dayjs;
  endTime: string | dayjs.Dayjs;
};

export enum TimeRangeStatusEnum {
  Pending,
  Underway,
  Expired,
}
export const getTimeRangeStatus = <T extends TimeRangeType>(range: T, now = dayjs()) => {
  if (now.isBefore(range.startTime)) {
    return TimeRangeStatusEnum.Pending;
  } else if (now.isSameOrAfter(range.startTime) && now.isSameOrBefore(range.endTime)) {
    return TimeRangeStatusEnum.Underway;
  } else if (now.isAfter(range.endTime)) {
    return TimeRangeStatusEnum.Expired;
  }
};

export const getTimeRangeDiff = (from: string | dayjs.Dayjs, to: string | dayjs.Dayjs) => {
  to = dayjs(to);
  const diff = dayjs(dayjs(to).diff(from));
  const result = {
    hours: to.diff(from, 'hours'),
    minutes: diff,
    seconds: diff,
  };
  return result;
};

export const useFromTimer = (
  fromInit: dayjs.Dayjs | string,
  nowInit: dayjs.Dayjs | string = dayjs(),
  keepTime = false
) => {
  const [from, setFrom] = useState<dayjs.Dayjs>(dayjs(fromInit));
  // 对比时间戳
  const [contrastNow, setContrastNow] = useState<number>(new Date().getTime());
  // 参数时间 不变
  const [initNow, setInitNow] = useState<dayjs.Dayjs>(dayjs(nowInit));
  // 计算时间 变化
  const [now, setNow] = useState<dayjs.Dayjs>(dayjs(nowInit));

  const setFromFn = useCallback(
    (from: string | dayjs.Dayjs) => {
      setFrom(dayjs(from));
    },
    [setFrom]
  );

  const setNowFn = useCallback(
    (now: string | dayjs.Dayjs) => {
      setNow(dayjs(now));
      setInitNow(dayjs(now));
      setContrastNow(new Date().getTime());
    },
    [setNow, setInitNow, setContrastNow]
  );

  useEffect(() => {
    if (!keepTime) {
      const timer = setTimeout(() => {
        const secondsDiff = Math.floor((new Date().getTime() - contrastNow) / 1000);
        setNow(dayjs(initNow).add(secondsDiff, 'seconds'));
      }, 1e3);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [now, setNow, initNow, contrastNow, keepTime]);

  const diff = getTimeRangeDiff(from, now);

  return {
    setFrom: setFromFn,
    setNow: setNowFn,
    ...diff,
  };
};

export const noop = () => void 0;
export const identity = <T>(v: T) => v;
const genBooleanFilter = (bool: boolean) => () => bool;
export const trueFilter = genBooleanFilter(true);
export const falseFilter = genBooleanFilter(false);

/**
 * 关联订单扫码url
 * @param orderSn
 * @returns
 */
export const getBindOrderQrCodeUrl = (orderSn: string) =>
  `https://admin.veeyung.com/admin/s/f/w?oid=${orderSn}`;

export const safeJSONParse = (str?: string, fallback = {}) => {
  try {
    return JSON.parse(str);
  } catch (e) {
    return fallback;
  }
};

export const cleanUndefinedToNull = <T>(obj: T) => {
  const result = {} as T;
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      result[key] = obj[key] === undefined ? null : obj[key];
    }
  }
  return result;
};

export const arrayMoveMutate = <ValueType>(array: ValueType[], from: number, to: number) => {
  array.splice(to < 0 ? array.length + to : to, 0, array.splice(from, 1)[0]);
};

export const arrayMove = <ValueType>(array: ValueType[], from: number, to: number) => {
  array = array.slice();
  arrayMoveMutate(array, from, to);
  return array;
};

// 工具函数：生成日期范围
export function getDateRange(start, end) {
  const dates = [];
  let current = new Date(start);
  const endDate = new Date(end);
  while (current <= endDate) {
    dates.push(current.toISOString().slice(0, 10));
    current.setDate(current.getDate() + 1);
  }
  return dates;
}

type FieldMap<T, M extends Record<string, keyof T>> = {
  [K in keyof M]: T[M[K]];
};

type TreeNode<T, M extends Record<string, keyof T>> = T &
  FieldMap<T, M> & {
    children: TreeNode<T, M>[];
    parent: TreeNode<T, M>;
  };

export const listToTree = <T extends { id: ID; parentId: ID }, M extends Record<string, keyof T>>(
  list: T[],
  fieldNames: M = {} as M
) => {
  const map: Record<string, TreeNode<T, M>> = {};
  const tree: TreeNode<T, M>[] = [];

  // 初始化节点
  list.forEach(item => {
    map[item.id] = Object.assign(
      { ...item },

      Object.entries(fieldNames).reduce((acc, [a, b]) => {
        return {
          ...acc,
          [a]: item[b],
        };
      }, {})
    ) as TreeNode<T, M>;
  });

  list.forEach(item => {
    const node = map[item.id];
    if (map[item.parentId]) {
      // 父节点存在 → 挂到父节点
      map[item.parentId].children = map[item.parentId].children || [];
      node.parent = map[item.parentId];
      map[item.parentId].children.push(node);
    } else {
      // 根节点
      tree.push(node);
    }
  });

  return { tree, keyMap: map };
};

export const sortBySortOrder = <T extends { sortOrder: number; id: ID }>(a: T, b: T) => {
  if (a.sortOrder !== b.sortOrder) {
    return b.sortOrder - a.sortOrder;
  }
  return Number(a.id) - Number(b.id);
};

export const timestampStr = () => new Date().getTime().toString(36);

export const getFormFullFieldValues = <T>(form: FormInstance, data: T) => {
  const cleared = Object.keys(form.getFieldsValue(true)).reduce((acc, key) => {
    acc[key] = undefined;
    return acc;
  }, {} as T);
  return Object.assign(cleared, data);
};
